import { useEffect, useState } from "react";
import classes from "../../PaymentMethods.module.scss";
import braintree, { GooglePayment } from "braintree-web";
import { loadScript } from "@/utils/dom";
import { useDonationFormStore } from "@/stores";
import { trackError } from "@/utils/error-monitoring";
import { getAsset } from "@/utils/assets";
import { CURRENCY, GOOGLE_MERCHANT_ID, PAYMENT_ENVIRONMENT } from "@/config";
import { getCountryName } from "@/utils/countries";
import type { WalletButtonProps } from "@/payment/types";
import { useBraintree } from "./useBraintree";

export function getFirstNameAndLastName(name?: string): {
  firstName: string;
  lastName: string;
} {
  if (!name) {
    return { firstName: "", lastName: "" };
  }

  const [firstName, ...rest] = name.split(" ");

  const lastName = rest[rest.length - 1] || "";

  return { firstName, lastName };
}

export function BraintreeGooglePayButton(props: Readonly<WalletButtonProps>) {
  const { amount, onSuccess, onError } = props;
  const { braintreeClient } = useBraintree();

  const [isLoading, setIsLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [googlePaymentInstance, setGooglePaymentInstance] =
    useState<GooglePayment>();
  const {
    setGatewayPayload,
    setAddress,
    setIsManualAddress,
    setPersonalDetails,
  } = useDonationFormStore();

  useEffect(() => {
    if (!braintreeClient) return;

    const createInstance = async () => {
      const googleInstance = await braintree.googlePayment.create({
        client: braintreeClient,
        googleMerchantId: GOOGLE_MERCHANT_ID,
        googlePayVersion: 2,
      });

      setGooglePaymentInstance(googleInstance);
      return googleInstance;
    };

    // A content blocker or a network filter refusing Google's script is not a
    // defect: the donor can still pay by card or PayPal.
    const loadGooglePay = async (): Promise<boolean> => {
      try {
        await loadScript("https://pay.google.com/gp/p/js/pay.js", "google-pay");
        return true;
      } catch (error) {
        trackError(error as Error, { level: "warning" });
        return false;
      }
    };

    const checkGooglePayAvailability = async (
      googleInstance: GooglePayment,
    ) => {
      try {
        const baseRequest = googleInstance.createPaymentDataRequest();

        const googlePayClient = new google.payments.api.PaymentsClient({
          environment: PAYMENT_ENVIRONMENT,
        });

        const isReadyToPayRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: baseRequest.allowedPaymentMethods,
          existingPaymentMethodRequired: true,
        };
        const isReadyToPay =
          await googlePayClient.isReadyToPay(isReadyToPayRequest);

        if (isReadyToPay.result) {
          setIsAvailable(true);
        } else {
          setIsAvailable(false);
          console.warn("Google Pay is not available on this device/browser");
        }
      } catch (error) {
        console.warn("Error checking Google Pay availability:", error);
        setIsAvailable(false);
        trackError(error as Error);
      }
    };

    const initializeGooglePay = async () => {
      try {
        if (!(await loadGooglePay())) return;

        const googleInstance = await createInstance();
        await checkGooglePayAvailability(googleInstance);
      } catch (error) {
        console.error("Error initializing Google Pay:", error);
        setIsAvailable(false);
        trackError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeGooglePay();
  }, [braintreeClient]);

  const handleGooglePayment = async () => {
    if (!googlePaymentInstance || !isAvailable) {
      return;
    }

    setIsProcessing(true);

    try {
      const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest(
        {
          transactionInfo: {
            currencyCode: CURRENCY,
            totalPriceStatus: "FINAL",
            totalPrice: `${amount}`,
          },
          emailRequired: true,
        },
      );

      const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
      if (cardPaymentMethod?.type === "CARD") {
        cardPaymentMethod.parameters = {
          ...cardPaymentMethod.parameters,
          billingAddressRequired: true,
          billingAddressParameters: {
            format: "FULL",
            phoneNumberRequired: false,
          },
        };
      }
      const googlePayClient = new google.payments.api.PaymentsClient({
        environment: PAYMENT_ENVIRONMENT,
      });

      const paymentData =
        await googlePayClient.loadPaymentData(paymentDataRequest);

      const billingAddress =
        paymentData.paymentMethodData?.info?.billingAddress;
      const email = paymentData?.email || "";

      const country = getCountryName(billingAddress?.countryCode);

      const payload = await googlePaymentInstance.parseResponse(paymentData);
      if (!payload.nonce) {
        throw new Error("No nonce found in payment response");
      }

      setGatewayPayload({ nonce: payload.nonce });
      setAddress({
        "address-line-1": billingAddress?.address1 || "",
        "address-line-2": billingAddress?.address2 || "",
        city: billingAddress?.locality || "",
        region: billingAddress?.administrativeArea || "",
        "postal-code": billingAddress?.postalCode || "",
        country,
      });

      const { firstName, lastName } = getFirstNameAndLastName(
        billingAddress?.name,
      );

      setPersonalDetails({
        title: "",
        firstName,
        lastName,
        email,
        confirmEmail: email,
      });

      setIsManualAddress(true);
      onSuccess({ nonce: payload.nonce });
    } catch (error) {
      if (error instanceof Error) {
        if (
          error.name === "AbortError" ||
          (error as { statusCode?: string }).statusCode === "CANCELED"
        ) {
          return;
        }
      }

      console.error("Error in Google Pay processing:", error);
      trackError(error as Error);
      onError?.(error as Error);
    } finally {
      setIsProcessing(false);
    }
  };

  const disabled = isLoading || !isAvailable || isProcessing;

  return (
    <button
      type="button"
      className={classes.button}
      onClick={handleGooglePayment}
      disabled={disabled}
    >
      <span>Donate with</span>
      <img
        src={getAsset("/images/google-pay.svg")}
        className={classes.icon}
        alt="Google Pay"
        width={524}
        height={206}
      />
    </button>
  );
}
